import React, { Suspense } from 'react'
import classes from './RouteTemplateSettingsModal.module.scss'
import Modal from '@/components/ui/Modal'
import Typography from '@/components/ui/Typography'
import Button from '@/components/ui/Buttons/Button'
import { ReactComponent as SettingsIcon } from '@/assets/icons/cogs.svg'
import {
  curryAddNewConnector,
  curryAddNewParam,
  curryAddNewSecret,
  useRouteTemplateForm,
  useSystemOptions,
} from './RouteTemplateSettingsModal.hooks'
import { FieldArray, Form, Formik } from 'formik'
import FormLabel from '@/components/ui/FormLabel'
import { RouteType, RouteTypeOptions, RuntimeOptions } from '@/config/app'
import Autocomplete from '@/components/ui/Autocomplete'
import { TimezoneOptions } from '@/config/timezones'
import { Puff } from '@/components/ui/Loaders'
import CronInput from '@/components/CronInput'
import { EstimatedCost } from '@/components/EstimatedCost'
import ScheduleSection from '@/components/ScheduleSection'
import Switch from '@/components/ui/Switch'
import FormikInput from '@/components/formik/FormikInput'
import DeleteButton from '@/components/ui/Buttons/DeleteButton'
import { ParametersTypesOptions } from '@/features/templates'

export type RouteTemplateSettingsModalProps = { onClose: () => void; templateId: number }

export const RouteTemplateSettingsModal = ({ onClose, templateId }: RouteTemplateSettingsModalProps) => {
  const formikProps = useRouteTemplateForm(templateId, onClose)
  const systemOptions = useSystemOptions()

  // @ts-ignore
  return (
    <Formik {...formikProps}>
      {({ values, handleSubmit, isSubmitting, setFieldValue }) => (
        <Form>
          <Modal onClose={onClose} isOpen>
            <Modal.Header>
              <div>
                <Modal.Icon>
                  <SettingsIcon />
                </Modal.Icon>
                <Typography variant={'xl'}>Route template settings</Typography>
              </div>
              <div>
                <Modal.Close onClick={onClose} />
              </div>
            </Modal.Header>
            <Modal.Content>
              <div className={classes.root}>
                <Typography variant={'xl'}>General</Typography>
                <div>
                  <FormLabel>Language</FormLabel>
                  <Autocomplete
                    name={'runtime'}
                    options={RuntimeOptions}
                    value={values.runtime}
                    onChange={setFieldValue}
                    disableClearable
                    selectOnly
                  />
                </div>
                <div>
                  <FormLabel>Route type</FormLabel>
                  <Autocomplete
                    name={'type'}
                    options={RouteTypeOptions}
                    value={values.type}
                    onChange={setFieldValue}
                    disableClearable
                    selectOnly
                  />
                </div>
                {values.type === RouteType.Cron && (
                  <>
                    <div>
                      <Typography variant={'md'}>Schedule</Typography>
                      <ScheduleSection value={values.schedule} />
                      <br />
                      <CronInput name={'schedule'} />
                    </div>

                    <div>
                      <Typography variant={'md'}>Estimated cost</Typography>
                      <Suspense fallback={<Typography variant={'caption'}>Calculating...</Typography>}>
                        <EstimatedCost value={values.schedule} />
                      </Suspense>
                    </div>
                    <div>
                      <FormLabel>Schedule timezone</FormLabel>
                      <Autocomplete
                        name={'scheduleTimeZone'}
                        options={TimezoneOptions}
                        value={values.scheduleTimeZone}
                        onChange={setFieldValue}
                        disableClearable
                      />
                    </div>
                  </>
                )}
                <br />

                <Typography variant={'xl'}>Security</Typography>
                <div className={classes.switch}>
                  <Typography isBold>{'Auth enabled'}</Typography>
                  <Switch
                    checked={values.authEnabled}
                    onChange={(event) => setFieldValue('authEnabled', event.target.checked)}
                  />
                </div>
                <br />

                <Typography variant={'xl'}>Requirements</Typography>

                <Typography variant={'lg'}>Connectors</Typography>
                <FieldArray name="requirements.connectors">
                  {(helpers) => (
                    <div className="d-grid gap-2">
                      {values.requirements?.connectors?.map((requirement, i) => (
                        <div key={i} className="d-flex gap-2">
                          <div className="flex-grow-1">
                            <div className="d-flex gap-1">
                              <FormikInput
                                name={`requirements.connectors.${i}.key`}
                                label="Key"
                                className="flex-grow-1"
                              />
                              <div className="flex-grow-1">
                                <FormLabel>System</FormLabel>
                                <Autocomplete
                                  name={`requirements.connectors.${i}.system`}
                                  value={values?.requirements?.connectors?.[i].system}
                                  onChange={setFieldValue}
                                  options={systemOptions}
                                  disableClearable={true}
                                />
                              </div>
                            </div>
                            <FormikInput
                              name={`requirements.connectors.${i}.description`}
                              label="Description"
                              type="textarea"
                              multiline
                              minRows={3}
                            />
                          </div>
                          <div>
                            <DeleteButton
                              onClick={() => helpers.remove(i)}
                              className={classes.removeButton}
                              color="neutral"
                            />
                          </div>
                        </div>
                      ))}

                      <div>
                        <Button onClick={curryAddNewConnector(helpers)} variant="outlined" color="neutral">
                          Add connector
                        </Button>
                      </div>
                    </div>
                  )}
                </FieldArray>

                <Typography variant={'lg'}>Secrets</Typography>
                <FieldArray name="requirements.secrets">
                  {(helpers) => (
                    <div className="d-grid gap-2">
                      {values.requirements?.secrets?.map((requirement, i) => (
                        <div key={i} className="d-flex gap-2">
                          <div className="flex-grow-1">
                            <FormikInput name={`requirements.secrets.${i}.key`} label="Key" className="flex-grow-1" />
                            <FormikInput
                              name={`requirements.secrets.${i}.description`}
                              label="Description"
                              type="textarea"
                              multiline
                              minRows={3}
                            />
                          </div>
                          <div>
                            <DeleteButton
                              onClick={() => helpers.remove(i)}
                              className={classes.removeButton}
                              color="neutral"
                            />
                          </div>
                        </div>
                      ))}

                      <div>
                        <Button onClick={curryAddNewSecret(helpers)} variant="outlined" color="neutral">
                          Add secret
                        </Button>
                      </div>
                    </div>
                  )}
                </FieldArray>

                <Typography variant={'lg'}>Parameters</Typography>
                <FieldArray name="requirements.params">
                  {(helpers) => (
                    <div className="d-grid gap-2">
                      {values.requirements?.params?.map((requirement, i) => (
                        <div key={i} className="d-flex gap-2">
                          <div className="flex-grow-1">
                            <FormLabel>Type</FormLabel>
                            <Autocomplete
                              name={`requirements.params.${i}.type`}
                              options={ParametersTypesOptions}
                              value={values.requirements?.params?.[i].type}
                              onChange={setFieldValue}
                              disableClearable
                            />

                            <FormikInput name={`requirements.params.${i}.key`} label="Key" className="flex-grow-1" />
                            <FormikInput
                              name={`requirements.params.${i}.description`}
                              label="Description"
                              type="textarea"
                              multiline
                              minRows={3}
                            />
                          </div>
                          <div>
                            <DeleteButton
                              onClick={() => helpers.remove(i)}
                              className={classes.removeButton}
                              color="neutral"
                            />
                          </div>
                        </div>
                      ))}

                      <div>
                        <Button onClick={curryAddNewParam(helpers)} variant="outlined" color="neutral">
                          Add parameter
                        </Button>
                      </div>
                    </div>
                  )}
                </FieldArray>
                {/*<Typography variant={'xl'}>Template status</Typography>*/}
                {/*<Typography>Only published templates are accessible for other users to create routes</Typography>*/}
                {/*<div>*/}
                {/*  <FormLabel>Status</FormLabel>*/}
                {/*  <Autocomplete*/}
                {/*    name={'status'}*/}
                {/*    options={RouteTemplateStatusOptions}*/}
                {/*    value={values.status}*/}
                {/*    onChange={setFieldValue}*/}
                {/*    disableClearable*/}
                {/*    selectOnly*/}
                {/*  />*/}
                {/*</div>*/}
              </div>
            </Modal.Content>
            <Modal.Footer>
              <Button
                onClick={() => handleSubmit()}
                disabled={isSubmitting}
                StartIcon={isSubmitting ? <Puff /> : undefined}
              >
                Update route template
              </Button>
            </Modal.Footer>
          </Modal>
        </Form>
      )}
    </Formik>
  )
}
